import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { UserPlus, ChevronLeft, ChevronRight, Users } from "lucide-react";
import { Button, Card, Spinner } from "@nutrayan/ui";
import { formatIndianDate } from "@nutrayan/utils";
import { listAdmins, adminLevelLabel } from "@/lib/staff-api";
import { roleLabel } from "@/lib/permissions";
import { StatusBadge } from "@/components/StatusBadge";

const PAGE_SIZE = 20;

export default function StaffListPage() {
  const [page, setPage] = useState(0);
  const staff = useQuery({
    queryKey: ["admin", "staff", page],
    queryFn: () => listAdmins(page, PAGE_SIZE),
  });

  const rows = staff.data ?? [];
  const hasNext = rows.length === PAGE_SIZE; // no total in the DTO — infer from a full page

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-display-xl text-charcoal">Staff Registry</h1>
          <p className="text-body text-ink mt-1">
            Administrators are managed here — completely separate from customers.
          </p>
        </div>
        <Link to="/staff/new">
          <Button size="md">
            <UserPlus size={16} aria-hidden />
            Create admin
          </Button>
        </Link>
      </header>

      {staff.isPending ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : staff.isError ? (
        <Card>
          <p className="text-body text-terracotta">
            Couldn&apos;t load the staff registry. You may not have permission, or the backend is
            unavailable.
          </p>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center py-16 text-center">
          <span className="bg-sand text-muted flex h-14 w-14 items-center justify-center rounded-full">
            <Users size={26} aria-hidden />
          </span>
          <h2 className="font-display text-display-sm text-charcoal mt-4">No administrators yet</h2>
          <p className="text-body-sm text-ink mt-1 max-w-sm">
            Invite your first administrator. They&apos;ll receive a secure email to set their own
            password.
          </p>
          <Link to="/staff/new" className="mt-4">
            <Button size="md">Create admin</Button>
          </Link>
        </Card>
      ) : (
        <>
          <Card className="overflow-x-auto p-0">
            <table className="text-body-sm w-full min-w-[46rem]">
              <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
                <tr>
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Email</th>
                  <th className="px-4 py-2.5">Role</th>
                  <th className="px-4 py-2.5">Level</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Password</th>
                  <th className="px-4 py-2.5">Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} className="border-charcoal/5 hover:bg-sand/40 border-b">
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/staff/${a.id}`}
                        className="text-forest font-medium hover:underline"
                      >
                        {a.fullName ?? "—"}
                      </Link>
                    </td>
                    <td className="text-ink px-4 py-2.5">{a.email ?? "—"}</td>
                    <td className="text-ink px-4 py-2.5">{roleLabel(a.role)}</td>
                    <td className="text-ink px-4 py-2.5">{adminLevelLabel(a.adminLevel)}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex gap-1">
                        <StatusBadge status={a.status} />
                        {a.locked ? <StatusBadge status="LOCKED" /> : null}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={a.passwordStatus} />
                    </td>
                    <td className="text-ink px-4 py-2.5">
                      {a.createdAt ? formatIndianDate(a.createdAt) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="flex items-center justify-between">
            <p className="text-caption text-muted">Page {page + 1}</p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft size={15} aria-hidden /> Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <ChevronRight size={15} aria-hidden />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
